import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import BombiixAGGrid from '../../../../../Components/BombiixAGGrid';
import type { BombiixColumnDef } from '../../../../../Components/BombiixAGGrid';
import { CanvasCell, ProductNumberCell, RowSelectCell, SelectAllHeader } from './bulk-grid-cells';
import type { BulkAgRow, BulkGridHandlers, BulkSortOrder } from '../types';

export interface DesignBulkAgGridProps {
  rows: BulkAgRow[];
  columnCount: number;
  handlers: BulkGridHandlers;
  allSelected: boolean;
  someSelected: boolean;
  onToggleAll: () => void;
  loading: boolean;
  sortOrder: BulkSortOrder;
  onSortOrderChange: (_order: BulkSortOrder) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  loadingMore: boolean;
}

const SLOT_SIZE = 50;
const SLOT_GAP = 8;

/** Image slots a canvas column is sized to hold, per the Figma bulk-edit frame. */
const CANVAS_SLOTS = 6;

/** Canvas rows are taller than the grid's 40px default to fit a 50px image slot. */
const CANVAS_ROW_HEIGHT = 58;

/**
 * Fixed horizontal chrome inside a canvas cell: 12px AG cell padding + 20px
 * checkbox + 8px gap, then 8px gap + 24px comment icon + 12px AG cell padding.
 * The 12px paddings come from BombiixAGGrid's --ag-cell-horizontal-padding.
 */
const CANVAS_CELL_CHROME = 76;

/**
 * Every canvas column is the same width — wide enough for CANVAS_SLOTS images
 * plus the checkbox and comment icon. Columns keep flex:1 so they still share
 * any extra viewport width equally rather than leaving a gap, but can never fall
 * below the width a full image strip needs.
 */
const CANVAS_COLUMN_WIDTH = CANVAS_CELL_CHROME + CANVAS_SLOTS * (SLOT_SIZE + SLOT_GAP);

/**
 * Design Details bulk grid rendered with the shared BombiixAGGrid (the
 * master-design grid): pinned Select + Product Number columns, one column per
 * canvas (image strip + per-canvas checkbox + comment icon), a pinned download
 * column, and the bottom floating action bar (reset / save / download).
 * Row-level selection and comments are folded into each row so AG Grid re-renders
 * only the rows that change.
 */
const DesignBulkAgGrid: React.FC<DesignBulkAgGridProps> = ({
  rows,
  columnCount,
  handlers,
  allSelected,
  someSelected,
  onToggleAll,
  loading,
  sortOrder,
  onSortOrderChange,
  onLoadMore,
  hasMore,
  loadingMore,
}) => {
  const { t } = useTranslation('bulk-edit');

  const columnDefs = useMemo<BombiixColumnDef<BulkAgRow>[]>(() => {
    const canvasColumns: BombiixColumnDef<BulkAgRow>[] = Array.from({ length: columnCount }, (_, index) => ({
      headerName: t('CANVAS_N', { number: index + 1 }),
      colId: `canvas_${index}`,
      minWidth: CANVAS_COLUMN_WIDTH,
      flex: 1,
      sortable: false,
      cellRenderer: CanvasCell,
      cellProps: { canvasIndex: index, ...handlers },
    }));

    return [
      {
        headerName: '',
        colId: '_select',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        pinned: 'left',
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        headerComponent: SelectAllHeader,
        headerComponentParams: { allSelected, someSelected, onToggleAll },
        cellRenderer: RowSelectCell,
        cellProps: { ...handlers },
      },
      {
        headerName: t('PRODUCT_NUMBER'),
        colId: 'productNumber',
        field: 'productNumber',
        width: 150,
        minWidth: 150,
        pinned: 'left',
        sortable: true,
        /* The grid only holds the pages fetched so far, so AG Grid must not
           reorder them locally — sorting is a server round-trip via onSortChanged. */
        comparator: () => 0,
        sort: sortOrder,
        cellRenderer: ProductNumberCell,
        cellProps: { ...handlers },
      },
      ...canvasColumns,
    ];
  }, [columnCount, handlers, t, sortOrder, allSelected, someSelected, onToggleAll]);

  const getRowId = useCallback((row: BulkAgRow) => row.devOrderId, []);

  /**
   * Product Number is the only sortable column, so the first sorted column in the
   * event is always it. AG Grid allows a third "no sort" state; the server always
   * orders by product number, so that is folded back to ascending.
   */
  const onSortChanged = useCallback(
    (event: { api: { getColumnState: () => Array<{ colId: string; sort?: string | null }> } }): void => {
      const state = event.api.getColumnState().find((column) => column.colId === 'productNumber');
      onSortOrderChange(state?.sort === 'desc' ? 'desc' : 'asc');
    },
    [onSortOrderChange],
  );

  /**
   * `popupParent: document.body` — the column (3-dots) menu is otherwise appended inside the
   * grid's own scrolling container, where it overflows, forces a scrollbar and visibly shortens
   * the table. Portalling it to <body> keeps the menu out of the grid's layout entirely.
   */
  const gridOptions = useMemo(
    () => ({
      rowHeight: CANVAS_ROW_HEIGHT,
      suppressContextMenu: true,
      popupParent: typeof document !== 'undefined' ? document.body : null,
      onSortChanged,
    }),
    [onSortChanged],
  );

  return (
    <div className="design-bulk-ag-wrapper">
      <BombiixAGGrid<BulkAgRow>
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={getRowId}
        height="100%"
        loading={loading}
        sortable={false}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
        gridOptions={gridOptions}
        enableCellCopy
      />
    </div>
  );
};

export default DesignBulkAgGrid;
